import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { cn } from '../cn'
import { EmptyState } from './EmptyState'
import { Pagination } from './Pagination'
import { ScrollRegion } from './ScrollRegion'
import { Skeleton } from './Skeleton'
import { nextSort, type SortState, type SortValue, sortRows } from './tableSort'

export interface Column<Row> {
  id: string
  header: ReactNode
  cell: (row: Row) => ReactNode
  /** Client-side sort key. Its presence makes the column sortable. */
  sortValue?: (row: Row) => SortValue
  /** Sortable on the server (no `sortValue`); pair with `manualSort`. */
  sortable?: boolean
  align?: 'left' | 'right' | 'center'
  /** Extra classes on every body cell of this column. */
  className?: string
  headerClassName?: string
  /**
   * Label shown beside the value when rows stack into cards below `sm` (phones). Defaults
   * to `header` when that is a string; columns without one (actions) show the value alone.
   */
  label?: string
}

export type DataTablePagination =
  | { mode: 'client'; pageSize: number }
  | {
      mode: 'server'
      hasNext: boolean
      hasPrevious: boolean
      onNext: () => void
      onPrevious: () => void
      summary?: ReactNode
    }

export interface DataTableProps<Row> {
  columns: Column<Row>[]
  rows: readonly Row[]
  getRowId: (row: Row) => string
  /** Accessible table name; visually hidden unless `showCaption`. */
  caption: string
  showCaption?: boolean
  loading?: boolean
  /** Rendered when there are no rows ("No holdings match these filters"). */
  empty?: ReactNode
  onRowClick?: (row: Row) => void
  /** Controlled sort. Omit for internal state. */
  sort?: SortState | null
  defaultSort?: SortState | null
  onSortChange?: (sort: SortState) => void
  /** Rows arrive already sorted (server sorting): headers only report the change. */
  manualSort?: boolean
  pagination?: DataTablePagination
  className?: string
}

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' } as const

/*
 * Below `sm` the table stacks: every row becomes a card and every cell a "label  value" line
 * (the label is CSS generated content from `data-label`), so a 390 px phone never needs
 * sideways scrolling to reach the status or action columns. Explicit table roles keep the
 * semantics that `display: block` would otherwise drop.
 */
const STACK = {
  table: 'max-sm:block',
  head: 'max-sm:sr-only',
  body: 'max-sm:block',
  row: 'max-sm:block max-sm:px-4 max-sm:py-3',
  cell: 'max-sm:flex max-sm:items-baseline max-sm:gap-3 max-sm:px-0 max-sm:py-1 max-sm:text-left max-sm:before:w-24 max-sm:before:shrink-0 max-sm:before:text-[11px] max-sm:before:font-semibold max-sm:before:tracking-[0.06em] max-sm:before:text-[#5f7268] max-sm:before:uppercase max-sm:before:content-[attr(data-label)]',
} as const

/**
 * Explicit roles: redundant on a normal table, but `display: block` (the stacked phone
 * layout) makes some browsers drop the implicit table semantics.
 */
const ROLE = {
  table: { role: 'table' },
  rowgroup: { role: 'rowgroup' },
  row: { role: 'row' },
  columnheader: { role: 'columnheader' },
  cell: { role: 'cell' },
} as const

function columnLabel<Row>(column: Column<Row>): string | undefined {
  if (column.label) return column.label
  return typeof column.header === 'string' ? column.header : undefined
}
const INTERACTIVE = 'a,button,input,select,textarea,label,[role="button"]'

export function DataTable<Row>({
  columns,
  rows,
  getRowId,
  caption,
  showCaption = false,
  loading = false,
  empty,
  onRowClick,
  sort: controlledSort,
  defaultSort = null,
  onSortChange,
  manualSort = false,
  pagination,
  className,
}: DataTableProps<Row>) {
  const [internalSort, setInternalSort] = useState<SortState | null>(defaultSort)
  const sort = controlledSort === undefined ? internalSort : controlledSort
  const [page, setPage] = useState(0)

  const sorted = useMemo(() => {
    if (manualSort || !sort) return rows
    const column = columns.find((candidate) => candidate.id === sort.columnId)
    return column?.sortValue ? sortRows(rows, column.sortValue, sort.direction) : rows
  }, [rows, columns, sort, manualSort])

  const pageSize = pagination?.mode === 'client' ? pagination.pageSize : null
  const pageCount = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1
  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1)
  }, [page, pageCount])
  const visible = pageSize ? sorted.slice(page * pageSize, page * pageSize + pageSize) : sorted

  function changeSort(columnId: string) {
    const next = nextSort(sort, columnId)
    if (controlledSort === undefined) setInternalSort(next)
    setPage(0)
    onSortChange?.(next)
  }

  function activate(event: MouseEvent | KeyboardEvent, row: Row) {
    const target = event.target as HTMLElement
    const interactive = target.closest(INTERACTIVE)
    if (interactive && interactive !== event.currentTarget) return
    onRowClick?.(row)
  }

  return (
    <div className={cn('min-w-0', className)}>
      <ScrollRegion
        label={`${caption} (scrollable table)`}
        className="rounded-lg border border-line bg-surface"
      >
        <table
          {...ROLE.table}
          aria-busy={loading || undefined}
          className={cn('w-full border-collapse text-left text-sm', STACK.table)}
        >
          <caption
            className={cn(showCaption ? 'px-4 py-3 text-left text-sm font-semibold' : 'sr-only')}
          >
            {caption}
          </caption>
          <thead {...ROLE.rowgroup} className={STACK.head}>
            <tr {...ROLE.row} className="border-b border-line bg-[#f5f8f3]">
              {columns.map((column) => {
                const sortable = Boolean(column.sortValue) || Boolean(column.sortable)
                const active = sort?.columnId === column.id
                const ariaSort = active
                  ? sort?.direction === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : undefined
                return (
                  <th
                    key={column.id}
                    {...ROLE.columnheader}
                    scope="col"
                    aria-sort={sortable ? (ariaSort ?? 'none') : undefined}
                    className={cn(
                      'px-4 py-3 text-xs font-semibold tracking-[0.06em] whitespace-nowrap text-[#5f7268] uppercase',
                      ALIGN[column.align ?? 'left'],
                      column.headerClassName,
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => changeSort(column.id)}
                        className={cn(
                          '-mx-1 inline-flex items-center gap-1.5 rounded-sm px-1 uppercase hover:text-teal',
                          active && 'text-teal',
                        )}
                      >
                        {column.header}
                        <SortIcon state={active ? sort?.direction : undefined} />
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody {...ROLE.rowgroup} className={STACK.body}>
            {loading ? (
              <LoadingRows columns={columns.length} />
            ) : visible.length === 0 ? (
              <tr {...ROLE.row} className={STACK.row}>
                <td {...ROLE.cell} colSpan={columns.length} className="p-0 max-sm:block">
                  {empty ?? <EmptyState variant="plain" title="No records to show" />}
                </td>
              </tr>
            ) : (
              visible.map((row) => (
                <tr
                  key={getRowId(row)}
                  {...ROLE.row}
                  onClick={onRowClick ? (event) => activate(event, row) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return
                          if (event.target !== event.currentTarget) return
                          event.preventDefault()
                          activate(event, row)
                        }
                      : undefined
                  }
                  tabIndex={onRowClick ? 0 : undefined}
                  className={cn(
                    'border-b border-line last:border-b-0 transition-colors',
                    STACK.row,
                    onRowClick &&
                      'cursor-pointer hover:bg-[#f6f9f5] focus-visible:bg-[#f6f9f5] focus-visible:outline-offset-[-2px]',
                  )}
                >
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      {...ROLE.cell}
                      data-label={columnLabel(column)}
                      className={cn(
                        'px-4 py-4 align-middle leading-relaxed text-ink',
                        ALIGN[column.align ?? 'left'],
                        column.className,
                        STACK.cell,
                      )}
                    >
                      <div className="min-w-0 max-sm:flex-1">{column.cell(row)}</div>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </ScrollRegion>
      {pagination?.mode === 'client' && sorted.length > pagination.pageSize ? (
        <Pagination
          className="mt-3"
          hasPrevious={page > 0}
          hasNext={page < pageCount - 1}
          onPrevious={() => setPage((current) => Math.max(0, current - 1))}
          onNext={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
          summary={`Showing ${page * pagination.pageSize + 1}–${Math.min(sorted.length, (page + 1) * pagination.pageSize)} of ${sorted.length}`}
        />
      ) : null}
      {pagination?.mode === 'server' ? (
        <Pagination
          className="mt-3"
          hasPrevious={pagination.hasPrevious}
          hasNext={pagination.hasNext}
          onPrevious={pagination.onPrevious}
          onNext={pagination.onNext}
          summary={pagination.summary}
          busy={loading}
        />
      ) : null}
    </div>
  )
}

function SortIcon({ state }: { state: 'asc' | 'desc' | undefined }) {
  const Icon = state === 'asc' ? ArrowUp : state === 'desc' ? ArrowDown : ArrowUpDown
  return <Icon aria-hidden="true" className={cn('size-3.5', !state && 'opacity-45')} />
}

function LoadingRows({ columns }: { columns: number }) {
  return (
    <>
      {[0, 1, 2, 3, 4].map((row) => (
        <tr key={row} className="border-b border-line last:border-b-0">
          {Array.from({ length: columns }, (_, column) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton cells
            <td key={column} className="px-4 py-4">
              <Skeleton className={cn('h-3.5', column === 0 ? 'w-4/5' : 'w-3/5')} />
            </td>
          ))}
        </tr>
      ))}
      <tr className="sr-only">
        <td colSpan={columns}>Loading…</td>
      </tr>
    </>
  )
}
